import { useEffect, useState } from 'react';
import { FileText, ImageIcon, Loader2, X } from 'lucide-react';
import { attachmentUrl, fetchAttachmentUrl } from '../attachments';
import { AttachmentPreview } from './AttachmentPreview';
import type { ChatAttachment } from '../hooks/useSoteriaChat';
import type { PendingAttachment } from '../hooks/useAttachmentUploads';
import { cn } from '@/lib/utils';

/** "412 KB" / "1.4 MB" — enough for an advisor to recognise the right file. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** One file the advisor attached, drawn inside their own message bubble.
 *
 * An image shows a thumbnail, because recognising a screenshot or a photographed
 * ID by its filename is hopeless; a document shows its name and size, which is
 * how it is actually identified. Either way clicking opens the real file.
 *
 * The bytes are never in the transcript payload — they are fetched per file from
 * the attachments endpoint, so scrolling a long history costs a row of metadata
 * per attachment rather than the attachments themselves.
 */
export function AttachmentChip({ attachment }: { attachment: ChatAttachment }) {
  const isImage = attachment.mime_type.startsWith('image/');
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Thumbnails load on mount; a document's bytes wait until it is opened, so a
  // conversation full of PDFs fetches nothing it is not showing.
  useEffect(() => {
    if (!isImage) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    fetchAttachmentUrl(attachment.id)
      .then((next) => {
        objectUrl = next;
        // Revoke rather than set state on an unmounted component — otherwise a
        // fast scroll through history leaks a blob per image it passed.
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        setUrl(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, isImage]);

  // A real link, not a scripted popup.
  //
  // This used to be a button that fetched the bytes and called window.open on
  // the resulting blob URL. For a document that meant awaiting the fetch first,
  // and an await ENDS the user-gesture window — so the open() that followed was
  // an unsolicited popup and the browser blocked it. Clicking a PDF did nothing
  // at all, with no error: the file looked locked.
  //
  // An <a> has no such problem, is never popup-blocked, and gives middle-click,
  // cmd-click and "open in new tab" for free, which a <button> cannot. The
  // endpoint already answers with the right content type and
  // `Content-Disposition: inline`, so the browser renders the file rather than
  // downloading it.
  const href = attachmentUrl(attachment.id);

  // Preview in place on a plain click; let every other click be a link.
  //
  // A new tab for a two-second look at the statement you just sent is the whole
  // task's worth of friction, so the default becomes the overlay. But the tab is
  // still what some people want — printing, a second monitor, keeping it open
  // beside the chat — and cmd/ctrl/shift/middle-click are how they ask for it.
  // Swallowing those would trade one annoyance for a worse one, so they fall
  // through to the browser untouched.
  const openPreview = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
      return;
    e.preventDefault();
    setPreviewOpen(true);
  };

  const preview = (
    <AttachmentPreview
      attachment={attachment}
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      preloadedUrl={url}
    />
  );

  if (isImage && url) {
    return (
      <>
        <a
          onClick={openPreview}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={`${attachment.filename} · ${formatSize(attachment.size_bytes)}`}
          className="block overflow-hidden rounded-lg border border-border transition-colors hover:border-primary/40"
        >
          {/* The thumbnail still renders from the object URL fetched on mount:
            an <img> is a subresource, and going through fetch() lets a failure
            become the named chip below instead of a broken-image icon. */}
          <img
            src={url}
            alt={attachment.filename}
            className="max-h-44 max-w-[15rem] bg-muted object-contain"
          />
        </a>
        {preview}
      </>
    );
  }

  // A document, an image still loading, or an image whose bytes could not be
  // fetched — all read better as a named chip than as a broken thumbnail.
  const body = (
    <>
      {isImage ? (
        <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-foreground">
          {attachment.filename}
        </span>
        <span className="block text-[11px] text-muted-foreground">
          {failed
            ? 'unavailable'
            : loading
              ? 'loading…'
              : formatSize(attachment.size_bytes)}
        </span>
      </span>
    </>
  );

  const chipClass =
    'inline-flex max-w-[18rem] items-center gap-2 rounded-lg border border-border bg-muted px-2.5 py-1.5 text-left transition-colors';

  // A file whose bytes could not be fetched is not a link to anywhere. An <a>
  // has no disabled state, so the dead case renders as plain text rather than
  // as something that looks clickable and is not.
  if (failed) {
    return (
      <span
        title="This file is no longer available"
        className={cn(chipClass, 'opacity-60')}
      >
        {body}
      </span>
    );
  }

  return (
    <>
      <a
        onClick={openPreview}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={attachment.filename}
        className={cn(chipClass, 'hover:bg-accent')}
      >
        {body}
      </a>
      {preview}
    </>
  );
}

/** The files on one turn, laid out in the message bubble. */
export function AttachmentList({
  attachments,
}: {
  attachments: Array<ChatAttachment>;
}) {
  if (!attachments.length) return null;
  return (
    <div className="mb-2 flex flex-wrap items-start gap-2">
      {attachments.map((a) => (
        <AttachmentChip key={a.id} attachment={a} />
      ))}
    </div>
  );
}

/** Files staged in the composer, before the message is sent.
 *
 * Distinct from AttachmentChip on purpose: these have no server id yet, they
 * show transfer state, and each is removable. An image previews from the local
 * File — there is nothing to fetch, and waiting for a round trip to show the
 * user the picture they just picked would feel broken.
 */
export function PendingAttachmentTray({
  attachments,
  onRemove,
}: {
  attachments: Array<PendingAttachment>;
  onRemove?: (key: string) => void;
}) {
  if (!attachments.length) return null;
  return (
    <div className="flex w-full flex-wrap items-start gap-2">
      {attachments.map((a) => {
        const failed = a.status === 'error';
        return (
          <div
            key={a.key}
            title={
              failed ? a.error : `${a.filename} · ${formatSize(a.size_bytes)}`
            }
            className={cn(
              'group/att relative flex max-w-[16rem] items-center gap-2 rounded-lg border px-2 py-1.5',
              failed
                ? 'border-destructive/30 bg-destructive/10'
                : 'border-border bg-muted',
            )}
          >
            {a.previewUrl ? (
              <img
                src={a.previewUrl}
                alt={a.filename}
                className="h-8 w-8 shrink-0 rounded object-cover"
              />
            ) : a.mime_type.startsWith('image/') ? (
              <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}

            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-foreground">
                {a.filename}
              </span>
              <span
                className={cn(
                  'block text-[11px]',
                  failed ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {failed
                  ? (a.error ?? 'could not attach')
                  : a.status === 'uploading'
                    ? 'attaching…'
                    : formatSize(a.size_bytes)}
              </span>
            </span>

            {a.status === 'uploading' && (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
            )}

            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(a.key)}
                aria-label={`Remove ${a.filename}`}
                className="ml-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
